/*
 * upsell-container.js
 *
 * Behavior for the three interactive children of the upsell container blocks
 * (blocks/upsell-container.liquid, blocks/cart_upsell-container.liquid,
 * blocks/upsell-container-pro.liquid):
 *
 *   <upsell-counter>       live "{count} selected · +{total}" readout
 *   <upsell-add-all>       one click that selects / adds every remaining card
 *   <upsell-arrows-proxy>  forwards clicks to the container's real Splide arrows
 *
 * All three resolve their working set the same way: walk up to the nearest
 * `[data-upsell-container]` root and query `.upsell` cards inside it. Both card
 * tracks carry that class - Track A (snippets/upsell-block.liquid, rendered as
 * <cart-drawer-upsell> / <product-info-upsell>) and Track B
 * (snippets/upsell-item-card.liquid) - so nothing here needs to know which card
 * snippet produced the markup.
 *
 * Every customElements.define is guarded: the cart drawer re-renders its whole
 * subtree on add/remove, which re-executes any inline block <script>, and a
 * second unguarded define() throws and kills the rest of the file.
 *
 * Nothing in here may throw when a block is missing or the cart re-renders
 * mid-flight - the container is a merchant-composed slot, so any given child
 * may simply not be there.
 */
(function () {
  if (!window.customElements) return;

  var SCOPE_SELECTOR = '[data-upsell-container]';
  var CARD_SELECTOR = '.upsell';
  var UNSELECTED_SELECTOR = '.upsell:not([data-selected="true"])';
  var TOGGLE_SELECTOR = '.upsell-toggle-btn';

  var CHECK_STAGGER = 60; /* ms between toggle clicks in mode "check" */
  var ADD_STAGGER = 250; /* mode "add" posts to /cart/add - give each one room */
  var CLICK_DEBOUNCE = 300;

  /* ------------------------------------------------------------------ utils */

  function scopeOf(el) {
    if (!el || !el.closest) return null;
    return el.closest(SCOPE_SELECTOR);
  }

  function list(root, selector) {
    if (!root || !root.querySelectorAll) return [];
    return Array.prototype.slice.call(root.querySelectorAll(selector));
  }

  function isSelected(card) {
    return !!card && card.getAttribute('data-selected') === 'true';
  }

  /* `data-price` / `data-compare-price` are only rendered when the merchant
     switched price updates on, and Liquid emits them as raw cents - sometimes
     as a float ("2495.0") because the discount math runs through `times:`. */
  function attrCents(card, name) {
    if (!card || !card.getAttribute) return null;
    var raw = card.getAttribute(name);
    if (raw === null || raw === '') return null;
    var n = parseFloat(raw);
    return isFinite(n) ? Math.round(n) : null;
  }

  /* "$24.95", "24,95 €", "£1,234.50" -> integer cents. A trailing group of one
     or two digits behind the last separator is the fractional part; anything
     else (".", "," as a thousands separator) folds into the whole number. */
  function parseMoneyText(text) {
    if (!text) return null;
    var digits = String(text).replace(/[^\d.,]/g, '');
    if (!digits) return null;

    var sep = Math.max(digits.lastIndexOf(','), digits.lastIndexOf('.'));
    var decimals = sep === -1 ? '' : digits.slice(sep + 1);
    var whole;
    var frac;

    if (sep !== -1 && decimals.length > 0 && decimals.length <= 2) {
      whole = digits.slice(0, sep).replace(/[^\d]/g, '');
      frac = (decimals + '00').slice(0, 2);
    } else {
      whole = digits.replace(/[^\d]/g, '');
      frac = '00';
    }

    if (!whole) whole = '0';
    var cents = parseInt(whole, 10) * 100 + parseInt(frac, 10);
    return isFinite(cents) ? cents : null;
  }

  function priceOf(card) {
    var cents = attrCents(card, 'data-price');
    if (cents !== null) return cents;
    var el = card.querySelector('.upsell__price .regular-price') || card.querySelector('.regular-price');
    return el ? parseMoneyText(el.textContent) : null;
  }

  function compareOf(card) {
    var cents = attrCents(card, 'data-compare-price');
    if (cents !== null) return cents;
    var el = card.querySelector('.upsell__price .compare-price') || card.querySelector('.compare-price');
    /* upsell-block.liquid renders the compare price with `hidden` when there is
       no discount, rather than omitting it. */
    if (!el || el.classList.contains('hidden')) return null;
    return parseMoneyText(el.textContent);
  }

  /* Money format: the container root publishes shop.money_format on
     `data-uc-money-format`. Without it, rebuild a plausible format out of the
     non-numeric characters wrapped around the first rendered price. */
  function moneyFormatFor(scope) {
    if (!scope) return null;

    /* A card's own format wins over the container's. When "Show prices without
       decimals" is on, the card emits an amount_no_decimals format, and the
       running total has to match the prices it is adding up — otherwise the
       cards read $49 and the counter reads $49.00. */
    var card = scope.querySelector('.upsell[data-money-format]');
    var cardFormat = card && card.getAttribute('data-money-format');
    if (cardFormat) return cardFormat;

    var declared = scope.getAttribute('data-uc-money-format');
    if (declared) return declared;

    var sample = scope.querySelector('.regular-price');
    var text = sample ? String(sample.textContent).trim() : '';
    if (!text) return null;

    var prefix = (text.match(/^[^\d]*/) || [''])[0];
    var suffix = (text.match(/[^\d]*$/) || [''])[0];
    prefix = prefix.replace(/[\s ]+$/, '');
    suffix = suffix.replace(/^[.,]/, '');
    if (!prefix && !suffix) prefix = '$';
    return prefix + '{{amount}}' + suffix;
  }

  /* Prefers the theme's own Shopify.formatMoney, falls back to a minimal
     {{amount}} template renderer. Tags are stripped so the result is safe to
     hand to textContent. */
  function money(cents, format) {
    cents = Math.round(cents || 0);

    if (window.Shopify && typeof window.Shopify.formatMoney === 'function' && format) {
      try {
        return String(window.Shopify.formatMoney(cents, format)).replace(/<[^>]*>/g, '');
      } catch (e) {
        /* fall through to the local renderer */
      }
    }

    var value = (cents / 100).toFixed(2);
    var noDec = String(Math.round(cents / 100));
    if (!format) return '$' + value;

    return format
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, noDec.replace(/\B(?=(\d{3})+(?!\d))/g, '.'))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, value.replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.'))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDec.replace(/\B(?=(\d{3})+(?!\d))/g, ','))
      .replace(/\{\{\s*amount\s*\}\}/g, value.replace(/\B(?=(\d{3})+(?!\d))/g, ','))
      .replace(/<[^>]*>/g, '');
  }

  function fillTokens(template, vars) {
    return String(template == null ? '' : template).replace(/\{(count|total|savings)\}/g, function (match, key) {
      return vars[key] == null ? '' : vars[key];
    });
  }

  /* Selection summary for one container. Amounts stay in integer cents. */
  function summarize(scope) {
    var cards = list(scope, CARD_SELECTOR);
    var count = 0;
    var total = 0;
    var savings = 0;

    cards.forEach(function (card) {
      if (!isSelected(card)) return;
      count++;
      var price = priceOf(card);
      var compare = compareOf(card);
      if (price !== null) total += price;
      if (price !== null && compare !== null && compare > price) savings += compare - price;
    });

    return { cards: cards.length, count: count, total: total, savings: savings };
  }

  /* One recompute per animation frame, however many triggers fired. */
  function makeScheduler(fn) {
    var frame = null;
    return {
      request: function () {
        if (frame !== null) return;
        frame = requestAnimationFrame(function () {
          frame = null;
          try {
            fn();
          } catch (e) {
            /* never let a half-rendered cart break the page */
          }
        });
      },
      cancel: function () {
        if (frame === null) return;
        cancelAnimationFrame(frame);
        frame = null;
      },
    };
  }

  /* Shared wiring: recompute on clicks inside the container, on `cart:refresh`,
     and whenever a card's `data-selected` flips or the list is re-rendered. */
  function observeScope(host, onChange) {
    var scope = scopeOf(host);
    var scheduler = makeScheduler(onChange);
    var handler = function () {
      scheduler.request();
    };
    var observer = null;

    if (scope) {
      scope.addEventListener('click', handler);
      if (window.MutationObserver) {
        observer = new MutationObserver(handler);
        try {
          observer.observe(scope, {
            subtree: true,
            childList: true,
            attributes: true,
            attributeFilter: ['data-selected'],
          });
        } catch (e) {
          observer = null;
        }
      }
    }
    document.addEventListener('cart:refresh', handler);

    scheduler.request();

    return {
      scope: scope,
      teardown: function () {
        scheduler.cancel();
        if (scope) scope.removeEventListener('click', handler);
        document.removeEventListener('cart:refresh', handler);
        if (observer) observer.disconnect();
        observer = null;
      },
    };
  }

  /* ------------------------------------------------------------ <upsell-counter> */

  function UpsellCounter() {
    return Reflect.construct(HTMLElement, [], UpsellCounter);
  }
  UpsellCounter.prototype = Object.create(HTMLElement.prototype);
  UpsellCounter.prototype.constructor = UpsellCounter;
  Object.setPrototypeOf(UpsellCounter, HTMLElement);

  UpsellCounter.prototype.connectedCallback = function () {
    var self = this;
    this._binding = observeScope(this, function () {
      self.update();
    });
  };

  UpsellCounter.prototype.disconnectedCallback = function () {
    if (this._binding) this._binding.teardown();
    this._binding = null;
  };

  UpsellCounter.prototype.textTarget = function () {
    var el = this.querySelector('.upsell-counter__text');
    if (el) return el;
    /* The block normally renders the span itself; create it only as a
       last resort so a stripped-down markup variant still gets a readout. */
    el = document.createElement('span');
    el.className = 'upsell-counter__text';
    this.appendChild(el);
    return el;
  };

  UpsellCounter.prototype.update = function () {
    var binding = this._binding;
    var scope = binding ? binding.scope : scopeOf(this);
    if (!scope) return;

    var summary = summarize(scope);
    var format = moneyFormatFor(scope);
    var vars = {
      count: String(summary.count),
      total: money(summary.total, format),
      /* No discount anywhere in the selection renders as nothing at all,
         so "you save {savings}" collapses instead of reading "you save $0.00".
         Templates that want the zero can say {total}. */
      savings: summary.savings > 0 ? money(summary.savings, format) : '',
    };

    var template = summary.count === 0 && this.hasAttribute('data-empty-text')
      ? this.getAttribute('data-empty-text')
      : this.getAttribute('data-template');

    var text = fillTokens(template, vars);
    var target = this.textTarget();
    /* Only write on a real change: the MutationObserver above watches this
       subtree, so an unconditional write would schedule another frame. */
    if (target.textContent !== text) target.textContent = text;

    /* The block's own CSS hides the counter when it is empty and the merchant
       asked for that, keyed off these two attributes. Write them here so the
       "hide when nothing is selected" setting isn't inert. Guarded on change
       for the same reason the text write above is. */
    var countAttr = String(summary.count);
    var emptyAttr = summary.count === 0 ? 'true' : 'false';
    if (this.getAttribute('data-count') !== countAttr) {
      this.setAttribute('data-count', countAttr);
    }
    if (this.getAttribute('data-empty') !== emptyAttr) {
      this.setAttribute('data-empty', emptyAttr);
    }

    this.dispatchSelectionChange(scope, summary);
  };

  UpsellCounter.prototype.dispatchSelectionChange = function (scope, summary) {
    var signature = summary.count + '|' + summary.total + '|' + summary.savings;
    if (this._signature === signature) return;
    this._signature = signature;

    try {
      scope.dispatchEvent(
        new CustomEvent('upsell:selection-change', {
          bubbles: true,
          composed: true,
          detail: {
            count: summary.count,
            total: summary.total,
            savings: summary.savings,
            blockId: scope.getAttribute('data-upsell-container') || null,
          },
        })
      );
    } catch (e) {
      /* CustomEvent is universally available in the browsers this theme
         supports; swallow anyway rather than break the readout. */
    }
  };

  if (!customElements.get('upsell-counter')) {
    customElements.define('upsell-counter', UpsellCounter);
  }

  /* ----------------------------------------------------------- <upsell-add-all> */

  function UpsellAddAll() {
    return Reflect.construct(HTMLElement, [], UpsellAddAll);
  }
  UpsellAddAll.prototype = Object.create(HTMLElement.prototype);
  UpsellAddAll.prototype.constructor = UpsellAddAll;
  Object.setPrototypeOf(UpsellAddAll, HTMLElement);

  UpsellAddAll.prototype.connectedCallback = function () {
    var self = this;
    this._busy = false;
    this._lastRun = 0;
    this._timer = null;

    this._onClick = function (event) {
      self.run(event);
    };
    this.addEventListener('click', this._onClick);

    this._binding = observeScope(this, function () {
      self.syncVisibility();
    });
  };

  UpsellAddAll.prototype.disconnectedCallback = function () {
    if (this._onClick) this.removeEventListener('click', this._onClick);
    this._onClick = null;
    if (this._binding) this._binding.teardown();
    this._binding = null;
    if (this._timer) clearTimeout(this._timer);
    this._timer = null;
    this._busy = false;
  };

  UpsellAddAll.prototype.buttons = function () {
    return list(this, 'button');
  };

  UpsellAddAll.prototype.setBusy = function (busy) {
    this._busy = !!busy;
    if (busy) {
      this.setAttribute('disabled', '');
      this.setAttribute('aria-busy', 'true');
    } else {
      this.removeAttribute('disabled');
      this.removeAttribute('aria-busy');
    }
    this.buttons().forEach(function (button) {
      if (busy) button.setAttribute('disabled', '');
      else button.removeAttribute('disabled');
    });
  };

  /* Nothing left to add - fold the button away entirely. Both the class and the
     attribute, so it disappears whether or not the block's CSS styles .hidden. */
  UpsellAddAll.prototype.syncVisibility = function () {
    var binding = this._binding;
    var scope = binding ? binding.scope : scopeOf(this);
    if (!scope) return;

    var remaining = list(scope, UNSELECTED_SELECTOR).length;
    var hasCards = list(scope, CARD_SELECTOR).length > 0;
    var hide = hasCards && remaining === 0;

    this.classList.toggle('hidden', hide);
    if (hide) this.setAttribute('hidden', '');
    else this.removeAttribute('hidden');
  };

  UpsellAddAll.prototype.run = function (event) {
    var now = Date.now();
    if (this._busy || now - this._lastRun < CLICK_DEBOUNCE) {
      if (event && event.preventDefault) event.preventDefault();
      return;
    }
    this._lastRun = now;

    var scope = scopeOf(this);
    if (!scope) return;

    /* Snapshot which products still need action, by id rather than by node:
       adding to the cart re-renders the drawer, which detaches every card
       element captured up front. */
    var pending = list(scope, UNSELECTED_SELECTOR)
      .map(function (card) {
        return card.getAttribute('data-product-id') || card.getAttribute('data-id') || card.getAttribute('data-handle');
      })
      .filter(function (id) {
        return !!id;
      });

    if (!pending.length) {
      this.syncVisibility();
      return;
    }

    var mode = this.getAttribute('data-mode') === 'add' ? 'add' : 'check';
    this.setBusy(true);
    this.step(pending, 0, mode);
  };

  UpsellAddAll.prototype.resolveCard = function (id) {
    var scope = scopeOf(this);
    if (!scope) return null;
    var selectors = [
      '.upsell[data-product-id="' + id + '"]',
      '.upsell[data-id="' + id + '"]',
      '.upsell[data-handle="' + id + '"]',
    ];
    for (var i = 0; i < selectors.length; i++) {
      var card;
      try {
        card = scope.querySelector(selectors[i]);
      } catch (e) {
        card = null;
      }
      if (card) return card;
    }
    return null;
  };

  UpsellAddAll.prototype.step = function (pending, index, mode) {
    var self = this;

    if (!this.isConnected || index >= pending.length) {
      this.setBusy(false);
      this.syncVisibility();
      return;
    }

    var card = this.resolveCard(pending[index]);
    if (card && !isSelected(card)) {
      if (mode === 'add') this.submitCard(card);
      else this.toggleCard(card);
    }

    this._timer = setTimeout(function () {
      self._timer = null;
      self.step(pending, index + 1, mode);
    }, mode === 'add' ? ADD_STAGGER : CHECK_STAGGER);
  };

  /* mode "check": the card's own toggle element. Depending on the merchant's
     `toggle_element` setting `.upsell-toggle-btn` is either the switch/checkbox
     button or the whole `.upsell__container` - clicking either is correct. */
  UpsellAddAll.prototype.toggleCard = function (card) {
    var toggle = card.querySelector(TOGGLE_SELECTOR);
    if (!toggle || toggle.hasAttribute('disabled')) return;
    if (typeof toggle.click === 'function') toggle.click();
  };

  /* mode "add": the add-button card style has no toggle, only a <product-form>
     wrapping a real product form whose visible submit lives outside it (bound
     via the form= attribute) plus a hidden in-form submit button. Clicking the
     hidden button is what makes ProductForm intercept the submit event. */
  UpsellAddAll.prototype.submitCard = function (card) {
    var host = card.querySelector('product-form');
    var form = host ? host.querySelector('form') : card.querySelector('form[data-type="add-to-cart-form"]');
    if (!form) return;

    var submit = form.querySelector('button[type="submit"], input[type="submit"]');
    if (submit && !submit.hasAttribute('disabled') && typeof submit.click === 'function') {
      submit.click();
      return;
    }

    /* No submit control rendered (sold out, or a forked card snippet) - ask the
       form directly, still going through the submit event ProductForm listens
       for. `form.submit()` would bypass that listener and navigate away. */
    if (typeof form.requestSubmit === 'function') {
      try {
        form.requestSubmit();
        return;
      } catch (e) {
        /* fall through */
      }
    }
    try {
      form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    } catch (e) {
      /* give up quietly */
    }
  };

  if (!customElements.get('upsell-add-all')) {
    customElements.define('upsell-add-all', UpsellAddAll);
  }

  /* ------------------------------------------------------ <upsell-arrows-proxy> */

  /* The container's own slider, never a card's. snippets/upsell-block.liquid
     renders its own `.splide` inside a card when the merchant picked slider
     stacking, and that one is nested *inside* the container's splide. */
  function containerSplide(scope) {
    if (!scope) return null;
    var own = scope.querySelector('.upsell-container__splide');
    if (own) return own;
    var all = list(scope, '.splide');
    for (var i = 0; i < all.length; i++) {
      if (!all[i].closest(CARD_SELECTOR)) return all[i];
    }
    return null;
  }

  function realArrow(splide, direction, exclude) {
    var candidates = list(splide, '.splide__arrow--' + direction);
    for (var i = 0; i < candidates.length; i++) {
      var arrow = candidates[i];
      if (exclude && exclude.contains(arrow)) continue;
      if (arrow.closest('.splide') !== splide) continue; /* a nested card slider's arrow */
      return arrow;
    }
    return null;
  }

  function UpsellArrowsProxy() {
    return Reflect.construct(HTMLElement, [], UpsellArrowsProxy);
  }
  UpsellArrowsProxy.prototype = Object.create(HTMLElement.prototype);
  UpsellArrowsProxy.prototype.constructor = UpsellArrowsProxy;
  Object.setPrototypeOf(UpsellArrowsProxy, HTMLElement);

  UpsellArrowsProxy.prototype.connectedCallback = function () {
    var self = this;
    this._pairs = [];
    this._mirror = null;
    this._waiting = null;

    this._proxyPrev = this.querySelector('.upsell-arrows__btn--prev');
    this._proxyNext = this.querySelector('.upsell-arrows__btn--next');

    var splide = containerSplide(scopeOf(this));
    /* No slider at all: this breakpoint's layout is a grid, or the arrows block
       was dropped into a non-slider container. Nothing to proxy - stay inert. */
    if (!splide) return;
    this._splide = splide;

    if (this.bind()) return;

    /* Splide generates its arrows on mount, which can land after this element
       upgrades. Watch the slider until they show up, then stop watching. */
    if (!window.MutationObserver) return;
    this._waiting = new MutationObserver(function () {
      if (self.bind() && self._waiting) {
        self._waiting.disconnect();
        self._waiting = null;
      }
    });
    try {
      this._waiting.observe(splide, { subtree: true, childList: true });
    } catch (e) {
      this._waiting = null;
    }
  };

  UpsellArrowsProxy.prototype.disconnectedCallback = function () {
    (this._pairs || []).forEach(function (pair) {
      pair.proxy.removeEventListener('click', pair.handler);
    });
    this._pairs = [];
    if (this._mirror) this._mirror.disconnect();
    this._mirror = null;
    if (this._waiting) this._waiting.disconnect();
    this._waiting = null;
    this._splide = null;
  };

  UpsellArrowsProxy.prototype.bind = function () {
    if (this._pairs && this._pairs.length) return true;
    var splide = this._splide;
    if (!splide) return false;

    var pairs = [];
    var self = this;

    [['prev', this._proxyPrev], ['next', this._proxyNext]].forEach(function (entry) {
      var proxy = entry[1];
      if (!proxy) return;
      var real = realArrow(splide, entry[0], self);
      if (!real) return;

      var handler = function (event) {
        if (event && event.preventDefault) event.preventDefault();
        if (real.isConnected && typeof real.click === 'function') real.click();
      };
      proxy.addEventListener('click', handler);
      pairs.push({ proxy: proxy, real: real, handler: handler });
    });

    if (!pairs.length) return false;
    this._pairs = pairs;
    this.mirror();

    /* Splide flips `disabled` on its arrows at the ends of a non-loop slider;
       carry that onto the proxies so they grey out in step. */
    if (!window.MutationObserver) return true;
    this._mirror = new MutationObserver(function () {
      self.mirror();
    });
    pairs.forEach(function (pair) {
      try {
        self._mirror.observe(pair.real, { attributes: true, attributeFilter: ['disabled'] });
      } catch (e) {
        /* skip this arrow */
      }
    });
    return true;
  };

  UpsellArrowsProxy.prototype.mirror = function () {
    (this._pairs || []).forEach(function (pair) {
      if (pair.real.hasAttribute('disabled')) pair.proxy.setAttribute('disabled', '');
      else pair.proxy.removeAttribute('disabled');
    });
  };

  if (!customElements.get('upsell-arrows-proxy')) {
    customElements.define('upsell-arrows-proxy', UpsellArrowsProxy);
  }
})();
